/**
 * Chart of Accounts helpers: form validation, edit diffing, and hierarchy
 * lookups. Validation mirrors the backend's GLBusinessLayer.validateAccountUpdate
 * so most mistakes are caught before a round-trip; the backend stays authoritative.
 */

import * as types from '../api/types';

export type NormalBalance = 'DEBIT' | 'CREDIT';

export const GL_ACCOUNT_TYPE_OPTIONS: types.GLAccountType[] = [
  types.GLAccountType.ASSET,
  types.GLAccountType.LIABILITY,
  types.GLAccountType.EQUITY,
  types.GLAccountType.REVENUE,
  types.GLAccountType.EXPENSE,
];

export const NORMAL_BALANCE_OPTIONS: NormalBalance[] = ['DEBIT', 'CREDIT'];

export const ACCOUNT_NUMBER_MAX_LENGTH = 20;
export const ACCOUNT_NAME_MAX_LENGTH = 100;

/** Conventional normal balance: assets and expenses are debit-normal, the rest credit-normal. */
export function defaultNormalBalance(accountType: types.GLAccountType): NormalBalance {
  return accountType === types.GLAccountType.ASSET || accountType === types.GLAccountType.EXPENSE
    ? 'DEBIT'
    : 'CREDIT';
}

/** Title-case an enum value for display: 'LIABILITY' -> 'Liability'. */
export function formatEnumLabel(value: string): string {
  return value.charAt(0) + value.slice(1).toLowerCase();
}

export function sortByAccountNumber(accounts: types.GLAccount[]): types.GLAccount[] {
  return [...accounts].sort((a, b) =>
    a.accountNumber.localeCompare(b.accountNumber, undefined, { numeric: true })
  );
}

/** IDs of every account below `accountId` in the parent hierarchy. */
export function getDescendantIds(accounts: types.GLAccount[], accountId: string): Set<string> {
  const childrenByParent = new Map<string, string[]>();
  for (const account of accounts) {
    if (!account.parentId) continue;
    const list = childrenByParent.get(account.parentId) ?? [];
    list.push(account.id);
    childrenByParent.set(account.parentId, list);
  }

  const descendants = new Set<string>();
  const stack = [...(childrenByParent.get(accountId) ?? [])];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (descendants.has(id)) continue;
    descendants.add(id);
    stack.push(...(childrenByParent.get(id) ?? []));
  }
  return descendants;
}

/** Accounts that may be chosen as the parent of `editingId` (all, when creating). */
export function getParentOptions(accounts: types.GLAccount[], editingId: string | null): types.GLAccount[] {
  if (!editingId) return sortByAccountNumber(accounts);
  const excluded = getDescendantIds(accounts, editingId);
  excluded.add(editingId);
  return sortByAccountNumber(accounts.filter((a) => !excluded.has(a.id)));
}

export interface GLAccountFormState {
  accountNumber: string;
  accountName: string;
  accountType: types.GLAccountType;
  normalBalance: NormalBalance;
  isPostable: boolean;
  /** '' means no parent. */
  parentId: string;
}

export const EMPTY_GL_ACCOUNT_FORM: GLAccountFormState = {
  accountNumber: '',
  accountName: '',
  accountType: types.GLAccountType.ASSET,
  normalBalance: 'DEBIT',
  isPostable: true,
  parentId: '',
};

export function formFromAccount(account: types.GLAccount): GLAccountFormState {
  return {
    accountNumber: account.accountNumber,
    accountName: account.accountName,
    accountType: account.accountType,
    normalBalance: account.normalBalance,
    isPostable: account.isPostable,
    parentId: account.parentId ?? '',
  };
}

/**
 * Validate the form. `editingId` is the account being edited (null when creating),
 * used to allow keeping its own number. Returns an error message or null.
 */
export function validateGLAccountForm(
  form: GLAccountFormState,
  accounts: types.GLAccount[],
  editingId: string | null
): string | null {
  const accountNumber = form.accountNumber.trim();
  const accountName = form.accountName.trim();

  if (!accountNumber) return 'Account number is required';
  if (accountNumber.length > ACCOUNT_NUMBER_MAX_LENGTH) {
    return `Account number must be ${ACCOUNT_NUMBER_MAX_LENGTH} characters or less`;
  }
  if (!accountName) return 'Account name is required';
  if (accountName.length > ACCOUNT_NAME_MAX_LENGTH) {
    return `Account name must be ${ACCOUNT_NAME_MAX_LENGTH} characters or less`;
  }

  // POST /gl/accounts is an upsert on accountNumber, so a duplicate number would
  // silently overwrite the existing account rather than fail. Block it here.
  const duplicate = accounts.find((a) => a.accountNumber === accountNumber && a.id !== editingId);
  if (duplicate) {
    return `Account number ${accountNumber} is already used by ${duplicate.accountName}`;
  }

  if (form.parentId && editingId) {
    if (form.parentId === editingId) return 'An account cannot be its own parent';
    if (getDescendantIds(accounts, editingId).has(form.parentId)) {
      return 'Parent account cannot be a sub-account of this account';
    }
  }

  return null;
}

export function buildCreateRequest(form: GLAccountFormState): types.CreateGLAccountRequest {
  return {
    accountNumber: form.accountNumber.trim(),
    accountName: form.accountName.trim(),
    accountType: form.accountType,
    normalBalance: form.normalBalance,
    isPostable: form.isPostable,
    ...(form.parentId ? { parentId: form.parentId } : {}),
  };
}

/**
 * PATCH body containing only the fields that changed. Locked fields
 * (number/type/normal balance) are never sent for an account with journal lines.
 */
export function buildUpdateRequest(
  form: GLAccountFormState,
  account: types.GLAccount
): types.UpdateGLAccountRequest {
  const request: types.UpdateGLAccountRequest = {};
  const accountName = form.accountName.trim();
  const accountNumber = form.accountNumber.trim();

  if (accountName !== account.accountName) request.accountName = accountName;
  if (form.isPostable !== account.isPostable) request.isPostable = form.isPostable;
  if (form.parentId !== (account.parentId ?? '')) request.parentId = form.parentId || null;

  if (!account.hasJournalLines) {
    if (accountNumber !== account.accountNumber) request.accountNumber = accountNumber;
    if (form.accountType !== account.accountType) request.accountType = form.accountType;
    if (form.normalBalance !== account.normalBalance) request.normalBalance = form.normalBalance;
  }

  return request;
}
